<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import type { SurveyQuestion } from '@/types/surveyTypes'
import { Field, ErrorMessage } from 'vee-validate'
import { useSurvey, AnswerTarget } from '@/composables/onlineSurvey/useSurvey'
import { useI18n } from '@/i18n'

const { setAnswer, answers } = useSurvey()
const { t } = useI18n()

const props = defineProps<{
  question: SurveyQuestion
  answerTarget?: AnswerTarget
}>()

const fieldName = computed(() => props.question.id)

const handleInput = (value: string) => {
  setAnswer(props.question.id, props.question.label, value || null, props.answerTarget)
}

const initializeValue = () => {
  const existingAnswer = answers.value.find((a) => a.id === props.question.id)
  if (existingAnswer !== undefined) {
    return existingAnswer.answer
  }
  return undefined
}

onMounted(() => {
  initializeValue()
})

watch(
  () => answers.value.find((a) => a.id === props.question.id),
  () => {
    initializeValue()
  },
)

const isTextarea = computed(() => props.question?.subtype === 'textarea')
</script>

<template>
  <div class="columns is-centered">
    <div class="column is-8 is-12-mobile">
      <div class="field">
        <div class="control">
          <Field
            :id="props.question.id"
            :name="fieldName"
            :value="initializeValue()"
            type="text"
            :placeholder="props.question.placeholder || t('answerPlaceholder')"
            @update:modelValue="handleInput"
            v-slot="{ field }"
          >
            <input
              v-bind="field"
              type="text"
              class="input"
              :placeholder="props.question.placeholder || t('answerPlaceholder')"
              v-if="!isTextarea"
            />
            <textarea
              v-bind="field"
              :placeholder="props.question.placeholder || t('answerPlaceholder')"
              rows="5"
              v-if="isTextarea"
              class="textarea"
            ></textarea>
          </Field>
        </div>
        <ErrorMessage :name="fieldName" class="help is-danger" />
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../../../assets/main.css';

/* Unit mit CSS ::after */
.control[data-unit]::after {
  content: attr(data-unit);
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--bulma-text-weak, #7a7a7a);
  font-size: 0.875rem;
  font-weight: 500;
  pointer-events: none;
  z-index: 2;
}

.control[data-unit] {
  position: relative;
}

.input,
.textarea {
  background-color: var(--bulma-scheme-main, #ffffff);
  border: 2px solid var(--bulma-border, #dbdbdb);
  border-radius: 0.75rem;
  box-shadow: none;
  color: var(--bulma-text, #363636);
  transition: all 0.3s ease;
  height: auto;
  padding: calc(0.75em - 1px) 1rem;
}

.input.has-unit {
  padding-right: 3rem;
}

.input:hover,
.textarea:hover {
  border-color: color-mix(in srgb, var(--bulma-border) 80%, black);
  background-color: var(--bulma-scheme-main-bis, #fafafa);
}

.input:focus,
.textarea:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 0.25rem color-mix(in srgb, var(--primary-color) 15%, transparent);
  background-color: var(--bulma-scheme-main, #ffffff);
  outline: none;
}

.input::placeholder,
.textarea::placeholder {
  color: var(--bulma-text-weak, #7a7a7a);
  opacity: 0.7;
}

textarea {
  resize: vertical;
  min-height: 120px;
}
</style>
